import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Building2,
  AlertCircle,
  Eye,
  ExternalLink,
  Users,
  Database,
  CreditCard,
  DollarSign,
  Calculator,
  Clock,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, CurrencyBadge } from "@/components/StatusBadge";
import { Flag } from "@/components/Flag";
import { 
  getAllApprovals, 
  approveBankDetails, 
  rejectBankDetails,
  markLoanDiskAdded,
  verifyLoanDiskRequest,
  approveDisbursement,
  rejectDisbursementApproval,
  approveNetInHand,
  rejectNetInHandApproval,
  approveConsolidation,
  rejectConsolidation,
  listGuarantorLinkTasks,
  confirmGuarantorLinkTask,
  dismissGuarantorLinkTask
} from "@/lib/api";
import { reviewedBankDigits } from "@/lib/reviewedBankDigits";
import { toast } from "sonner";
import { BadgeCheck, Layers, ShieldCheck, ShieldAlert, UserPlus } from "lucide-react";
import { runPayeeNameCheck } from "@/lib/api";
import {
  BANK_DETAILS_APPROVAL_PERMISSIONS,
  CREDIT_APPROVAL_PERMISSIONS,
  DISBURSEMENT_APPROVER_PERMISSIONS,
} from "@/lib/permissions";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const curOf = (x) => ((x?.currency || x?.application?.currency) === "AUD" ? "A$" : "£");
const isAud = (x) => (x?.currency || x?.application?.currency) === "AUD";
const copOk = (s) => s === "full_match" || s === "match";

export default function Approvals() {
  const { user: currentUser, hasAnyPermission } = useAuth();
  const { currentRegion, regionConfig } = useRegion();
  // Each queue's tab and buttons are offered only to a holder of the permission its
  // endpoints check, so a button never answers 403. `src/pages/Approvals.permissionGate.test.js`
  // holds the page to it.
  const canApproveCredit = hasAnyPermission(CREDIT_APPROVAL_PERMISSIONS);
  const canApproveBankDetails = hasAnyPermission(BANK_DETAILS_APPROVAL_PERMISSIONS);
  const canApproveDisbursement = hasAnyPermission(DISBURSEMENT_APPROVER_PERMISSIONS);
  const [approvals, setApprovals] = useState({ 
    bank_details: [], 
    loandisk_requests: [], 
    disbursement_approvals: [], 
    net_in_hand_approvals: [],
    consolidation_approvals: []
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [payeeChecking, setPayeeChecking] = useState(null);
  const [userRole, setUserRole] = useState("credit_team"); // Default to credit_team
  
  // Rejection dialog state
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  
  // Net in Hand approval dialog state
  const [nihApprovalDialog, setNihApprovalDialog] = useState(false);
  const [nihApprovalAmount, setNihApprovalAmount] = useState("");

  // Guarantor Link Tasks — Paul's fail-safe for multi-director deals
  const [glTasks, setGlTasks] = useState([]);
  const [glProcessing, setGlProcessing] = useState(null);
  const glCount = glTasks.length;

  const loadGlTasks = async () => {
    try {
      const r = await listGuarantorLinkTasks("pending");
      setGlTasks(r?.tasks || []);
    } catch (_e) {
      // silent — tab just shows empty state
    }
  };

  const handleConfirmGl = async (taskId) => {
    setGlProcessing(taskId);
    try {
      await confirmGuarantorLinkTask(taskId, "Manually linked in LoanDisk");
      toast.success("Guarantor link marked as done");
      loadGlTasks();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to confirm"));
    } finally { setGlProcessing(null); }
  };

  const handleDismissGl = async (taskId) => {
    setGlProcessing(taskId);
    try {
      await dismissGuarantorLinkTask(taskId, "Not applicable");
      toast.info("Task dismissed");
      loadGlTasks();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to dismiss"));
    } finally { setGlProcessing(null); }
  };

  useEffect(() => {
    loadGlTasks();
  }, []);

  useEffect(() => {
    loadApprovals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, currentRegion]);

  const loadApprovals = async () => {
    try {
      setLoading(true);
      const data = await getAllApprovals(userRole, currentRegion);
      setApprovals(data);
    } catch (error) {
      console.error("Failed to load approvals:", error);
      toast.error("Failed to load approvals");
    } finally {
      setLoading(false);
    }
  };

  // Bank details approval handlers
  const handlePayeeCheck = async (benId) => {
    try {
      setPayeeChecking(benId);
      const r = await runPayeeNameCheck(benId);
      const pc = r.payee_check;
      setApprovals((prev) => ({
        ...prev,
        bank_details: prev.bank_details.map((it) =>
          it.beneficiary_id === benId ? { ...it, payee_check: pc } : it
        ),
      }));
      if (pc.status === "full_match") toast.success("Payee name matches the bank's records");
      else if (pc.status === "no_match") toast.error("Payee name does NOT match the bank's records");
      else toast.warning("Account valid — name could not be verified");
    } catch (error) {
      console.error("Payee check failed:", error);
      toast.error(errorDetailMessage(error, "Payee name check failed"));
    } finally {
      setPayeeChecking(null);
    }
  };

  // `reviewed` is what the row showed, from `reviewedBankDigits`; the server approves only
  // if those are still the row's digits.
  const handleApproveBankDetails = async (benId, reviewed) => {
    try {
      setProcessing(benId);
      const result = await approveBankDetails(benId, reviewed);
      
      if (result.action === "reused_existing") {
        toast.success("Existing verified beneficiary linked to application");
      } else {
        toast.success("Bank details approved and verified");
      }
      
      loadApprovals();
    } catch (error) {
      // Not the error object: an axios error carries the request body, which holds the
      // account number, and console output is recorded.
      console.error("Approval failed:", error.response?.status ?? error.name);
      toast.error(errorDetailMessage(error, "Failed to approve"));
      // The digits changed, or someone decided the row, after the page loaded; show
      // Credit what is there now.
      if ([400, 409].includes(error.response?.status)) loadApprovals();
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectBankDetails = async () => {
    if (!selectedItem || !rejectionReason.trim()) return;
    
    try {
      setProcessing(selectedItem.beneficiary_id);
      await rejectBankDetails(
        selectedItem.beneficiary_id,
        rejectionReason
      );
      
      toast.success("Bank details rejected");
      setRejectDialog(false);
      setSelectedItem(null);
      setRejectionReason("");
      loadApprovals();
    } catch (error) {
      // The status only: console output is recorded.
      console.error("Rejection failed:", error.response?.status ?? error.name);
      toast.error(errorDetailMessage(error, "Failed to reject"));
      // The row was decided, or is shared, after the page loaded; show what is there now.
      if ([400, 409].includes(error.response?.status)) loadApprovals();
    } finally {
      setProcessing(null);
    }
  };

  // LoanDisk approval handlers
  const handleMarkLoanDiskAdded = async (requestId) => {
    try {
      setProcessing(requestId);
      await markLoanDiskAdded(requestId);
      toast.success("Marked as added - pending credit team verification");
      loadApprovals();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to mark as added"));
    } finally {
      setProcessing(null);
    }
  };

  const handleVerifyLoanDisk = async (requestId) => {
    try {
      setProcessing(requestId);
      await verifyLoanDiskRequest(requestId);
      toast.success("LoanDisk entry verified");
      loadApprovals();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to verify"));
    } finally {
      setProcessing(null);
    }
  };

  // Disbursement approval handlers (formal flow)
  const handleApproveDisbursement = async (approvalId) => {
    try {
      setProcessing(approvalId);
      await approveDisbursement(approvalId);
      toast.success("Disbursement approved - user can now proceed with password verification");
      loadApprovals();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to approve disbursement"));
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectDisbursement = async (approvalId, reason) => {
    try {
      setProcessing(approvalId);
      await rejectDisbursementApproval(approvalId, reason);
      toast.success("Disbursement rejected");
      loadApprovals();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to reject disbursement"));
    } finally {
      setProcessing(null);
    }
  };

  // Net in Hand approval handlers
  const handleApproveNetInHand = async (approvalId, approvedAmount) => {
    try {
      setProcessing(approvalId);
      await approveNetInHand(approvalId, approvedAmount);
      toast.success("Net in Hand approved");
      setNihApprovalDialog(false);
      setSelectedItem(null);
      setNihApprovalAmount("");
      loadApprovals();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to approve"));
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectNetInHand = async (approvalId, reason) => {
    try {
      setProcessing(approvalId);
      await rejectNetInHandApproval(approvalId, reason);
      toast.success("Net in Hand rejected");
      loadApprovals();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to reject"));
    } finally {
      setProcessing(null);
    }
  };

  const openRejectDialog = (item) => {
    setSelectedItem(item);
    setRejectionReason("");
    setRejectDialog(true);
  };

  const openNihApprovalDialog = (item) => {
    setSelectedItem(item);
    setNihApprovalAmount(item.calculated_net_in_hand?.toString() || "");
    setNihApprovalDialog(true);
  };

  const bankDetailsCount = approvals.bank_details?.length || 0;
  const loandiskCount = approvals.loandisk_requests?.length || 0;
  const disbursementCount = approvals.disbursement_approvals?.length || 0;
  const nihCount = approvals.net_in_hand_approvals?.length || 0;
  const consolidationCount = approvals.consolidation_approvals?.length || 0;
  // First tab the user can act on: the credit queues for a credit approver, otherwise
  // the disbursement queue, otherwise the one list nobody's permission hides.
  let firstTab = "loandisk";
  if (canApproveBankDetails && bankDetailsCount > 0) firstTab = "bank-details";
  else if (loandiskCount > 0) firstTab = "loandisk";
  else if (canApproveCredit && nihCount > 0) firstTab = "netinhand";
  else if (canApproveDisbursement) firstTab = "disbursement";
  else if (canApproveBankDetails) firstTab = "bank-details";
  const totalCount = bankDetailsCount + loandiskCount + disbursementCount + nihCount + consolidationCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="approvals-page">
      <PageHeader
        title={`Approvals — ${regionConfig.name}`}
        description={`Review and approve pending ${regionConfig.name} items based on your role. Switch region (top right) to see the other queue.`}
      />

      {/* Role Selector */}
      <div className="mb-6 flex items-center gap-4">
        <Label>View as:</Label>
        <Select value={userRole} onValueChange={setUserRole}>
          <SelectTrigger className="w-48" data-testid="role-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="credit_team">Credit Team</SelectItem>
            <SelectItem value="collections">Collections</SelectItem>
            <SelectItem value="admin">Admin (All)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {totalCount} pending item{totalCount !== 1 ? "s" : ""}
        </span>
      </div>

      {totalCount === 0 ? (
        <div className="empty-state">
          <CheckCircle className="empty-state-icon text-emerald-500" />
          <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
          <p className="text-muted-foreground">
            No pending approvals for your role at the moment.
          </p>
        </div>
      ) : (
        <Tabs defaultValue={firstTab} className="space-y-6">
          <TabsList>
            {canApproveBankDetails && (
              <TabsTrigger value="bank-details" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Bank Details
                {bankDetailsCount > 0 && (
                  <Badge variant="secondary" className="ml-1">{bankDetailsCount}</Badge>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="loandisk" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              LoanDisk
              {loandiskCount > 0 && (
                <Badge variant="secondary" className="ml-1">{loandiskCount}</Badge>
              )}
            </TabsTrigger>
            {canApproveCredit && (userRole === "credit_team" || userRole === "admin") && (
              <TabsTrigger value="netinhand" className="flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Net in Hand
                {nihCount > 0 && (
                  <Badge variant="destructive" className="ml-1">{nihCount}</Badge>
                )}
              </TabsTrigger>
            )}
            {canApproveDisbursement && (userRole === "credit_team" || userRole === "admin") && (
              <TabsTrigger value="disbursement" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Disbursement
                {disbursementCount > 0 && (
                  <Badge variant="destructive" className="ml-1">{disbursementCount}</Badge>
                )}
              </TabsTrigger>
            )}
            {canApproveCredit && (userRole === "credit_team" || userRole === "admin") && (
              <TabsTrigger value="consolidation" className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Consolidation
                {consolidationCount > 0 && (
                  <Badge variant="destructive" className="ml-1">{consolidationCount}</Badge>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="guarantor-links" className="flex items-center gap-2" data-testid="tab-guarantor-links">
              <UserPlus className="w-4 h-4" />
              Guarantor Links
              {glCount > 0 && (
                <Badge variant="destructive" className="ml-1" data-testid="gl-count-badge">{glCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Bank Details Tab */}
          {canApproveBankDetails && (
            <TabsContent value="bank-details" className="space-y-3">
              {bankDetailsCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No pending bank details approvals
                </div>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {approvals.bank_details.map((item) => {
                        const reviewed = reviewedBankDigits(item);
                        return (
                        <div 
                          key={item.beneficiary_id} 
                          data-testid={`bank-approval-${item.beneficiary_id}`}
                          className="p-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {/* Company & App ID */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                <span className="font-semibold text-sm truncate">{item.company_name}</span>
                                <Badge variant="outline" className="flex-shrink-0 text-xs">
                                  {item.application?.application_id}
                                </Badge>
                                {item.application?.deal_type && (
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs ${
                                      item.application.deal_type?.includes('Top') 
                                        ? 'border-amber-300 text-amber-700 bg-amber-50' 
                                        : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                                    }`}
                                  >
                                    {item.application.deal_type}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {item.submitted_by} • <Flag code={item.application?.country === "UK" ? "GB" : "AU"} size={14} /> {item.application?.company_number}
                              </div>
                            </div>
                          
                            {/* Loan Amount */}
                            <div className="text-right flex-shrink-0 w-20">
                              <p className="font-semibold text-sm">
                                {curOf(item)}{item.application?.loan_amount?.toLocaleString()}
                              </p>
                            </div>
                          
                            {/* Bank Details - compact */}
                            <div className="flex-shrink-0 bg-blue-50 rounded px-2 py-1 border border-blue-200">
                              <div className="flex gap-3 text-xs font-mono">
                                <div>
                                  <span className="text-muted-foreground">Acc:</span> {reviewed.account_number}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">{isAud(item) ? "BSB:" : "Sort:"}</span> {reviewed.sort_code}
                                </div>
                              </div>
                            </div>
                          
                            {/* Payee Name Verification (GoCardless PNV) */}
                            <div className="flex-shrink-0 w-44">
                              {item.payee_check ? (
                                <div
                                  data-testid={`payee-check-result-${item.beneficiary_id}`}
                                  title={(item.payee_check.reasons || []).join(" ")}
                                  className={`rounded px-2 py-1 border text-[11px] font-medium flex items-start gap-1 ${
                                    copOk(item.payee_check.status) && item.payee_check.policy_verdict !== "credit_review"
                                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                      : item.payee_check.status === "no_match"
                                      ? "bg-red-50 border-red-200 text-red-700"
                                      : "bg-amber-50 border-amber-200 text-amber-700"
                                  }`}
                                >
                                  {copOk(item.payee_check.status) ? (
                                    <ShieldCheck className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                                  ) : (
                                    <ShieldAlert className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                                  )}
                                  <span className="min-w-0">
                                    {item.payee_check.policy_verdict === "credit_review" && copOk(item.payee_check.status)
                                      ? "CoP passed — name differs from borrower"
                                      : copOk(item.payee_check.status)
                                      ? "Payee name matches"
                                      : item.payee_check.status === "no_match"
                                      ? "Name does NOT match"
                                      : "Name unverified"}
                                    <span className="block text-[10px] font-normal opacity-75 truncate">
                                      vs “{item.payee_check.checked_name}”
                                    </span>
                                    {!copOk(item.payee_check.status) && (item.payee_check.cop_suggested_name || item.payee_check.bank_legal_holder) && (
                                      <span className="block text-[10px] font-semibold font-mono mt-0.5" title="Account name held by the bank (Capay CoP)" data-testid={`payee-bank-name-${item.beneficiary_id}`}>
                                        bank: {item.payee_check.cop_suggested_name || item.payee_check.bank_legal_holder}
                                      </span>
                                    )}
                                    {(item.payee_check.reasons || []).filter((r) => /^[⚠🚩ℹ]/.test(r)).map((r, i) => (
                                      <span key={i} className="block text-[10px] font-normal text-amber-700 mt-0.5">{r}</span>
                                    ))}
                                  </span>
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-full text-xs"
                                  onClick={() => handlePayeeCheck(item.beneficiary_id)}
                                  disabled={payeeChecking === item.beneficiary_id}
                                  data-testid={`payee-check-btn-${item.beneficiary_id}`}
                                >
                                  {payeeChecking === item.beneficiary_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <ShieldCheck className="w-3 h-3 mr-1" />
                                  )}
                                  Verify payee name
                                </Button>
                              )}
                            </div>
                          
                            {/* Evidence */}
                            {item.evidence_url && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
                                <a href={item.evidence_url} target="_blank" rel="noopener noreferrer" title="View Evidence">
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              </Button>
                            )}
                          
                            {/* Actions */}
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => handleApproveBankDetails(item.beneficiary_id, reviewed)}
                                disabled={processing === item.beneficiary_id}
                                data-testid={`approve-bank-${item.beneficiary_id}`}
                                className="h-7 text-xs"
                              >
                                {processing === item.beneficiary_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openRejectDialog(item)}
                                disabled={processing === item.beneficiary_id}
                                data-testid={`reject-bank-${item.beneficiary_id}`}
                                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* LoanDisk Tab */}
          <TabsContent value="loandisk" className="space-y-3">
            {loandiskCount === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No pending LoanDisk requests
              </div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {approvals.loandisk_requests.map((item) => (
                      <div 
                        key={item.id} 
                        data-testid={`loandisk-approval-${item.id}`}
                        className="p-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {/* Type indicator */}
                          <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                            item.approval_type === "loandisk_add" ? "bg-blue-100" : "bg-emerald-100"
                          }`}>
                            <Database className={`w-4 h-4 ${
                              item.approval_type === "loandisk_add" ? "text-blue-600" : "text-emerald-600"
                            }`} />
                          </div>
                          
                          {/* Company & SF Number */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm truncate">{item.company_name}</span>
                              <Badge variant="outline" className="flex-shrink-0 text-xs font-mono">
                                {item.sf_number}
                              </Badge>
                              <Badge 
                                variant={item.approval_type === "loandisk_add" ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {item.approval_type === "loandisk_add" ? "Add" : "Verify"}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {item.requested_by} • {new Date(item.requested_at).toLocaleDateString("en-GB")}
                              {item.approval_type === "loandisk_verify" && item.added_by && (
                                <span className="text-blue-600"> • Added by {item.added_by}</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Loan Amount */}
                          <div className="text-right flex-shrink-0 w-24">
                            <p className="font-semibold text-sm">
                                {curOf(item)}{item.loan_amount?.toLocaleString()}
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-1.5 flex-shrink-0">
                            {item.approval_type === "loandisk_add" ? (
                              <Button
                                size="sm"
                                onClick={() => handleMarkLoanDiskAdded(item.id)}
                                disabled={processing === item.id}
                                data-testid={`mark-added-${item.id}`}
                                className="h-7 text-xs"
                              >
                                {processing === item.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Mark Added
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleVerifyLoanDisk(item.id)}
                                disabled={processing === item.id}
                                data-testid={`verify-loandisk-${item.id}`}
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                              >
                                {processing === item.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Verify
                              </Button>
                            )}
                            {item.application && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
                                <Link to={`/workflow/${item.application.id || item.application.application_id}`}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Link>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Net in Hand Tab */}
          {canApproveCredit && (
            <TabsContent value="netinhand" className="space-y-3">
              {nihCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No pending Net in Hand approvals
                </div>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {approvals.net_in_hand_approvals.map((item) => (
                        <div 
                          key={item.id} 
                          data-testid={`nih-approval-${item.id}`}
                          className="p-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {/* Icon */}
                            <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center flex-shrink-0">
                              <Calculator className="w-4 h-4 text-purple-600" />
                            </div>
                          
                            {/* Company & SF */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm truncate">{item.company_name}</span>
                                <Badge variant="outline" className="text-xs font-mono">{item.sf_number}</Badge>
                                <Badge variant="destructive" className="text-xs">Top Up</Badge>
                                {item.name_mismatch && (
                                  <Badge variant="outline" className="text-xs text-red-600 border-red-300">
                                    <AlertCircle className="w-3 h-3 mr-0.5" />Name Mismatch
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {item.requested_by} • {item.approval_reason}
                              </div>
                            </div>
                          
                            {/* Amounts - compact */}
                            <div className="flex-shrink-0 bg-slate-50 rounded px-2 py-1 border text-xs">
                              <div className="flex gap-3">
                                <div>
                                  <span className="text-muted-foreground">New:</span> {curOf(item)}{item.new_loan_amount?.toLocaleString()}
                                </div>
                                {item.existing_loan_found && (
                                  <div className="text-red-600">
                                    <span>-Outstanding:</span> {curOf(item)}{item.existing_outstanding_balance?.toLocaleString()}
                                  </div>
                                )}
                                <div className="text-emerald-700 font-semibold">
                                  <span>Net:</span> {curOf(item)}{item.calculated_net_in_hand?.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          
                            {/* Actions */}
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => openNihApprovalDialog(item)}
                                disabled={processing === item.id}
                                data-testid={`approve-nih-${item.id}`}
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                              >
                                {processing === item.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem({ ...item, type: 'netinhand' });
                                  setRejectionReason("");
                                  setRejectDialog(true);
                                }}
                                disabled={processing === item.id}
                                data-testid={`reject-nih-${item.id}`}
                                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                              {item.application && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
                                  <Link to={`/workflow/${item.application.id || item.application.application_id}`}>
                                    <Eye className="w-3.5 h-3.5" />
                                  </Link>
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Disbursement Tab - Formal Manager Sign-off */}
          {canApproveDisbursement && (
            <TabsContent value="disbursement" className="space-y-3">
              {disbursementCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No pending disbursement approvals
                </div>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {approvals.disbursement_approvals.map((item) => (
                        <div 
                          key={item.id} 
                          data-testid={`disbursement-approval-${item.id}`}
                          className={`p-3 hover:bg-slate-50 transition-colors ${
                            item.needs_revalidation || item.nih_expired || item.consolidation_expired 
                              ? 'bg-red-50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Icon */}
                            <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <DollarSign className="w-4 h-4 text-emerald-600" />
                            </div>
                          
                            {/* Company & SF */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm truncate">{item.company_name}</span>
                                <Badge variant="outline" className="text-xs font-mono">
                                  {item.application_sf || item.sf_number}
                                </Badge>
                                {(item.needs_revalidation || item.nih_expired || item.consolidation_expired) && (
                                  <Badge variant="destructive" className="text-xs">
                                    <AlertTriangle className="w-3 h-3 mr-0.5" />Expired
                                  </Badge>
                                )}
                                {item.consolidation_payments?.length > 0 && (
                                  <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                                    +{item.consolidation_payments.length} payoffs
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {item.requested_by_name || item.requested_by} • {new Date(item.requested_at).toLocaleDateString('en-GB')}
                              </div>
                            </div>
                          
                            {/* Amounts - compact */}
                            <div className="flex-shrink-0 bg-emerald-50 rounded px-2 py-1 border border-emerald-200 text-xs">
                              <div className="flex gap-3">
                                <div>
                                  <span className="text-muted-foreground">To Customer:</span>{' '}
                                  <span className="font-semibold">{curOf(item)}{item.disbursement_amount?.toLocaleString()}</span>
                                </div>
                                <div className="text-emerald-700 font-semibold">
                                  <span>Total:</span> {curOf(item)}{item.total_disbursement?.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          
                            {/* Time warning if applicable */}
                            {(item.nih_seconds_remaining || item.consolidation_seconds_remaining) && 
                             !item.nih_expired && !item.consolidation_expired && (
                              <div className="flex-shrink-0 text-xs text-amber-700">
                                <Clock className="w-3 h-3 inline mr-0.5" />
                                {item.nih_seconds_remaining && `${Math.floor(item.nih_seconds_remaining / 60)}m`}
                              </div>
                            )}
                          
                            {/* Actions */}
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => handleApproveDisbursement(item.id)}
                                disabled={processing === item.id || item.needs_revalidation || item.nih_expired || item.consolidation_expired}
                                data-testid={`approve-disbursement-${item.id}`}
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400"
                              >
                                {processing === item.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                {item.needs_revalidation || item.nih_expired || item.consolidation_expired ? "Expired" : "Approve"}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem({ ...item, type: 'disbursement' });
                                  setRejectionReason("");
                                  setRejectDialog(true);
                                }}
                                disabled={processing === item.id}
                                data-testid={`reject-disbursement-${item.id}`}
                                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                              {item.application && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
                                  <Link to={`/workflow/${item.application.id || item.application_id}`}>
                                    <Eye className="w-3.5 h-3.5" />
                                  </Link>
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Consolidation Tab */}
          {canApproveCredit && (
            <TabsContent value="consolidation" className="space-y-3">
              {consolidationCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No pending consolidation approvals
                </div>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {approvals.consolidation_approvals.map((item) => (
                        <div 
                          key={item.id} 
                          data-testid={`consolidation-approval-${item.id}`}
                          className="p-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {/* Icon */}
                            <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center flex-shrink-0">
                              <Layers className="w-4 h-4 text-purple-600" />
                            </div>
                          
                            {/* Company & SF */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm truncate">{item.company_name}</span>
                                <Badge variant="outline" className="text-xs font-mono">{item.application_sf}</Badge>
                                {!item.all_cops_verified && (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                    <AlertTriangle className="w-3 h-3 mr-0.5" />CoP Pending
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {item.lenders?.length || 0} lenders • {curOf(item)}{item.total_consolidation_amount?.toLocaleString()} payoffs
                              </div>
                            </div>
                          
                            {/* Amounts - compact */}
                            <div className="flex-shrink-0 bg-purple-50 rounded px-2 py-1 border border-purple-200 text-xs">
                              <div className="flex gap-3">
                                <div className="text-red-600">
                                  <span>Payoffs:</span> {curOf(item)}{item.total_consolidation_amount?.toLocaleString()}
                                </div>
                                <div className="text-emerald-700 font-semibold">
                                  <span>Net:</span> {curOf(item)}{item.net_in_hand_to_customer?.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          
                            {/* Actions */}
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    setProcessing(`approve-cons-${item.id}`);
                                    await approveConsolidation(item.id);
                                    toast.success("Consolidation approved");
                                    loadApprovals();
                                  } catch (error) {
                                    toast.error(errorDetailMessage(error, "Failed to approve"));
                                  } finally {
                                    setProcessing(null);
                                  }
                                }}
                                disabled={processing === `approve-cons-${item.id}`}
                                data-testid={`approve-consolidation-${item.id}`}
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                              >
                                {processing === `approve-cons-${item.id}` ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem({ ...item, type: 'consolidation' });
                                  setRejectionReason("");
                                  setRejectDialog(true);
                                }}
                                disabled={processing === `approve-cons-${item.id}`}
                                data-testid={`reject-consolidation-${item.id}`}
                                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
                                <Link to={`/workflow/${item.application_id}`}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Link>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Guarantor Links Tab (Paul's fail-safe) */}
          <TabsContent value="guarantor-links" className="space-y-3" data-testid="guarantor-links-tab">
            {glCount === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No pending guarantor-link tasks
              </div>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-amber-600" />
                    Manual guarantor attachments needed in LoanDisk
                  </CardTitle>
                  <CardDescription className="text-xs">
                    LoanDisk&apos;s API only links one guarantor per loan. When a deal has
                    multiple directors we create every PG as a borrower record, but the extras must
                    be attached to the loan manually via the LoanDisk UI. Tick them off here once done.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {glTasks.map((t) => (
                      <div
                        key={t.id}
                        className="p-4 hover:bg-slate-50/60 transition-colors"
                        data-testid={`gl-task-${t.id}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Building2 className="w-4 h-4 text-amber-600 shrink-0" />
                              <span className="font-semibold text-sm truncate">{t.company_name || "—"}</span>
                              <Badge variant="outline" className="text-xs">{t.sf_number}</Badge>
                              <Badge variant="outline" className="text-xs bg-slate-50">
                                Loan {t.loan_id}
                              </Badge>
                              <Badge variant="outline" className="text-xs bg-amber-50 border-amber-300 text-amber-700">
                                {t.additional_borrowers?.length || 0} extra PG{t.additional_borrowers?.length === 1 ? "" : "s"}
                              </Badge>
                            </div>
                            <div className="mt-1.5 pl-6 space-y-0.5 text-xs">
                              <div className="text-muted-foreground">
                                Main (already linked):{" "}
                                <span className="text-foreground">{t.main_borrower_name || "—"}</span>{" "}
                                <code className="text-[10px]">#{t.main_borrower_id}</code>
                              </div>
                              <div className="text-amber-700 font-medium">
                                Attach these to the loan manually:
                                <ul className="mt-0.5 space-y-0.5">
                                  {(t.additional_borrowers || []).map((g) => (
                                    <li key={g.borrower_id} className="text-foreground pl-3">
                                      • <span className="font-medium">{g.name || g.business_name || "—"}</span>{" "}
                                      <code className="text-[10px] text-muted-foreground">#{g.borrower_id}</code>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {t.email_error && (
                                <div className="text-red-600 text-[11px] mt-1">
                                  Email notification failed: {t.email_error}
                                </div>
                              )}
                              <div className="text-muted-foreground pt-1">
                                Opened {new Date(t.created_at).toLocaleString("en-GB")}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleConfirmGl(t.id)}
                              disabled={glProcessing === t.id}
                              data-testid={`gl-confirm-btn-${t.id}`}
                            >
                              {glProcessing === t.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <><CheckCircle className="w-3.5 h-3.5 mr-1" />Linked</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDismissGl(t.id)}
                              disabled={glProcessing === t.id}
                              data-testid={`gl-dismiss-btn-${t.id}`}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" />Dismiss
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedItem?.type === 'disbursement' ? 'Reject Disbursement' : 
               selectedItem?.type === 'netinhand' ? 'Reject Net in Hand' :
               selectedItem?.type === 'consolidation' ? 'Reject Consolidation' : 'Reject Bank Details'}
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection. The user can amend and resubmit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Textarea
                id="rejection-reason"
                placeholder={selectedItem?.type === 'disbursement' 
                  ? "e.g., Outstanding balance doesn't match records..." 
                  : selectedItem?.type === 'consolidation'
                  ? "e.g., Lender details need verification..."
                  : "e.g., Account name doesn't match company name..."}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                data-testid="rejection-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (selectedItem?.type === 'disbursement') {
                  handleRejectDisbursement(selectedItem.id, rejectionReason);
                  setRejectDialog(false);
                } else if (selectedItem?.type === 'netinhand') {
                  handleRejectNetInHand(selectedItem.id, rejectionReason);
                  setRejectDialog(false);
                } else if (selectedItem?.type === 'consolidation') {
                  try {
                    await rejectConsolidation(selectedItem.id, rejectionReason);
                    toast.success("Consolidation rejected");
                    setRejectDialog(false);
                    loadApprovals();
                  } catch (error) {
                    toast.error(errorDetailMessage(error, "Failed to reject"));
                  }
                } else {
                  handleRejectBankDetails();
                }
              }}
              disabled={!rejectionReason.trim() || processing}
              data-testid="confirm-reject-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Net in Hand Approval Dialog */}
      <Dialog open={nihApprovalDialog} onOpenChange={setNihApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Net in Hand</DialogTitle>
            <DialogDescription>
              Enter the approved Net in Hand amount for this Top Up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedItem && (
              <div className="p-4 bg-slate-50 rounded-lg border space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">New Loan:</span>
                  <span className="font-mono">£{selectedItem.new_loan_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Outstanding Balance:</span>
                  <span className="font-mono">-£{selectedItem.existing_outstanding_balance?.toLocaleString()}</span>
                </div>
                <div className="pt-2 border-t flex justify-between font-medium">
                  <span>Calculated:</span>
                  <span className="font-mono">£{selectedItem.calculated_net_in_hand?.toLocaleString()}</span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="approved-amount">Approved Net in Hand Amount (£)</Label>
              <Input
                id="approved-amount"
                type="number"
                step="0.01"
                value={nihApprovalAmount}
                onChange={(e) => setNihApprovalAmount(e.target.value)}
                placeholder="Enter amount..."
                data-testid="nih-approval-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNihApprovalDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleApproveNetInHand(selectedItem?.id, parseFloat(nihApprovalAmount))}
              disabled={!nihApprovalAmount || processing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-nih-approval-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
